import { useState, useRef } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Menu,
  Modal,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import {
  IconSettings,
  IconPlus,
  IconLayoutGrid,
  IconDotsVertical,
  IconFileDownload,
  IconAugmentedReality,
  IconFolderOpen,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { ArOverlay } from "../ar/ArOverlay";
import { ConnectShelf } from "../panels/ConnectShelf";
import { useConnected, useInstances } from "../store";
import { useWebSocket } from "../WebSocket";
import { UserMenu } from "./UserMenu";
import { useIsMobile } from "../hooks";
import { useArEntry } from "./useArEntry";
import { useCatalogImport } from "./useCatalogImport";
import { PublishModal } from "./PublishModal";

/**
 * Generate a cryptographically secure random session ID.
 * Uses crypto.getRandomValues() (128 bits of entropy).
 */
function generateSessionId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function ConnectionStatus({ compact = false }: { compact?: boolean }) {
  const connected = useConnected();
  const sessionId = typeof window !== "undefined" ? window.sessionId : null;

  // On a phone the header has no room for the session id and a worded badge;
  // the connection state is all that has to stay visible.
  if (compact) {
    return (
      <Tooltip label={connected ? "Connected" : "Disconnected"}>
        <Box
          aria-label={connected ? "Connected" : "Disconnected"}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: connected
              ? "var(--mantine-color-green-6)"
              : "var(--mantine-color-red-6)",
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Group gap="xs">
      {sessionId && (
        <Badge
          color="gray"
          variant="light"
          size="sm"
          style={{ fontFamily: "monospace" }}
        >
          Session: {sessionId}
        </Badge>
      )}
      <Badge
        color={connected ? "green" : "red"}
        variant="light"
        size="sm"
      >
        {connected ? "Connected" : "Disconnected"}
      </Badge>
    </Group>
  );
}

function Header() {
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [newSessionModalOpen, setNewSessionModalOpen] = useState(false);
  const [packageName, setPackageName] = useState("assembled_robot");
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const { sendClearAllInstances, sendExportUrdf, sendSaveConfig, sendLoadConfig } = useWebSocket();
  const instances = useInstances();
  const connected = useConnected();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    canUseAr,
    canUseLiveAr,
    arBusy,
    arError,
    setArError,
    liveArOpen,
    arLaunchHint,
    handleOpenLiveAr,
    handleCloseLiveAr,
    handleViewInAr,
  } = useArEntry();

  useCatalogImport(connected, sendLoadConfig);

  const handleClearAll = () => {
    sendClearAllInstances();
    setClearModalOpen(false);
  };

  const handleNewSession = () => {
    // Full reload (not client-side navigate) so the WebSocket connection
    // and in-memory store both start clean against the new session id,
    // rather than carrying over stale state from the old session.
    window.location.href = `/builder/${generateSessionId()}`;
  };

  const handleExport = () => {
    sendExportUrdf(packageName);
  };

  const handleSave = () => {
    sendSaveConfig("assembly_config");
  };

  const handleLoadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result;
        if (typeof content === "string") {
          sendLoadConfig(content);
        }
      };
      reader.readAsText(file);
      event.target.value = "";
    }
  };

  return (
    <>
      <Box
        style={{
          height: isMobile ? 52 : 48,
          flexShrink: 0,
          backgroundColor: "var(--mantine-color-body)",
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "0 8px" : "0 16px",
        }}
      >
        {isMobile ? (
          <>
            <Text size="md" fw={600}>
              AssemblyViewer
            </Text>
            <Group gap="xs">
              <ConnectionStatus compact />
              <UserMenu />
              {/* Every header action collapses into one menu: the row of
                  buttons the desktop shows needs ~700px of width. */}
              <Menu shadow="md" width={240} position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size={44}
                    aria-label="Builder actions"
                  >
                    <IconDotsVertical size={20} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconFolderOpen size={16} />}
                    onClick={handleLoadClick}
                  >
                    Load
                  </Menu.Item>
                  {instances.length > 0 && (
                    <>
                      <Menu.Divider />
                      {/* Plain child rather than a Menu.Item so typing the
                          package name does not close the menu. */}
                      <Box px="sm" py={6}>
                        <TextInput
                          size="xs"
                          label="Package name"
                          value={packageName}
                          onChange={(e) => setPackageName(e.currentTarget.value)}
                          placeholder="Package name"
                        />
                      </Box>
                      {canUseAr && (
                        <>
                          <Menu.Item
                            leftSection={<IconAugmentedReality size={16} />}
                            disabled={arBusy}
                            closeMenuOnClick={false}
                            onClick={() => handleViewInAr("floor")}
                          >
                            {arBusy ? "Preparing AR..." : "View in AR (floor)"}
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconAugmentedReality size={16} />}
                            disabled={arBusy}
                            closeMenuOnClick={false}
                            onClick={() => handleViewInAr("wall")}
                          >
                            {arBusy ? "Preparing AR..." : "View in AR (wall)"}
                          </Menu.Item>
                        </>
                      )}
                      {canUseLiveAr && (
                        <Menu.Item
                          leftSection={<IconAugmentedReality size={16} />}
                          onClick={() => void handleOpenLiveAr()}
                        >
                          Live AR (beta)
                        </Menu.Item>
                      )}
                      <Menu.Item
                        leftSection={<IconFileDownload size={16} />}
                        onClick={handleExport}
                      >
                        Export URDF
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconUpload size={16} />}
                        onClick={handleSave}
                      >
                        Save
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconUpload size={16} />}
                        onClick={() => setPublishModalOpen(true)}
                      >
                        Publish to Catalog
                      </Menu.Item>
                    </>
                  )}
                  <Menu.Divider />
                  <Menu.Item
                    leftSection={<IconLayoutGrid size={16} />}
                    onClick={() =>
                      navigate(`/catalog?return=${encodeURIComponent(window.sessionId ?? "")}`)
                    }
                  >
                    Catalog
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconSettings size={16} />}
                    disabled={instances.length === 0}
                    onClick={() =>
                      navigate(
                        `/configure?session=${window.sessionId}&package=${encodeURIComponent(packageName)}`
                      )
                    }
                  >
                    Config Generator
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setNewSessionModalOpen(true)}
                  >
                    New Session
                  </Menu.Item>
                  {instances.length > 0 && (
                    <>
                      <Menu.Divider />
                      <Menu.Item
                        color="red"
                        leftSection={<IconTrash size={16} />}
                        onClick={() => setClearModalOpen(true)}
                      >
                        Clear All
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </>
        ) : (
        <>
        <Group gap="md">
          <Text size="md" fw={600}>
            AssemblyViewer
          </Text>
          {instances.length > 0 && (
            <>
              <TextInput
                size="xs"
                value={packageName}
                onChange={(e) => setPackageName(e.currentTarget.value)}
                placeholder="Package name"
                style={{ width: 150 }}
              />
              <Button
                size="xs"
                color="blue"
                variant="light"
                onClick={handleExport}
              >
                Export URDF
              </Button>
              <Button
                size="xs"
                variant="light"
                onClick={handleSave}
              >
                Save
              </Button>
              {/* Quick Look support is a property of the browser, not of the
                  viewport, so this cannot live only in the mobile menu: an
                  iPad in landscape gets the desktop layout and would
                  otherwise have no way to reach AR. */}
              {canUseLiveAr && (
                <Tooltip label="Overlay the live assembly on the camera - joints stay movable">
                  <Button
                    size="xs"
                    variant="light"
                    color="indigo"
                    leftSection={<IconAugmentedReality size={14} />}
                    onClick={() => void handleOpenLiveAr()}
                  >
                    Live AR
                  </Button>
                </Tooltip>
              )}
              {canUseAr && (
                <Menu shadow="md" position="bottom-end">
                  <Menu.Target>
                    <Button
                      size="xs"
                      variant="light"
                      color="indigo"
                      loading={arBusy}
                      leftSection={<IconAugmentedReality size={14} />}
                    >
                      View in AR
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={() => handleViewInAr("floor")}>床・机に置く</Menu.Item>
                    <Menu.Item onClick={() => handleViewInAr("wall")}>壁に付ける</Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
              <Tooltip label="Publish this assembly to the public catalog">
                <Button
                  size="xs"
                  variant="light"
                  color="teal"
                  onClick={() => setPublishModalOpen(true)}
                >
                  Publish to Catalog
                </Button>
              </Tooltip>
            </>
          )}
          <Button
            size="xs"
            variant="light"
            onClick={handleLoadClick}
          >
            Load
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          {instances.length > 0 && (
            <Button
              size="xs"
              color="red"
              variant="light"
              onClick={() => setClearModalOpen(true)}
            >
              Clear All
            </Button>
          )}
        </Group>
        <Group gap="md">
          <Tooltip label="Browse assemblies published by users">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconLayoutGrid size={14} />}
              onClick={() =>
                // Carry the session along so the catalog can add an
                // entry back into this scene ("Add to Scene").
                navigate(`/catalog?return=${encodeURIComponent(window.sessionId ?? "")}`)
              }
            >
              Catalog
            </Button>
          </Tooltip>
          <Tooltip label="Generate MoveIt2, Gazebo, IL configs from current assembly">
            <Button
              size="xs"
              variant="light"
              color="grape"
              leftSection={<IconSettings size={14} />}
              onClick={() => navigate(`/configure?session=${window.sessionId}&package=${encodeURIComponent(packageName)}`)}
              disabled={instances.length === 0}
            >
              Config Generator
            </Button>
          </Tooltip>
          <Tooltip label="Start a fresh session with a new URL">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => setNewSessionModalOpen(true)}
            >
              New Session
            </Button>
          </Tooltip>
          <UserMenu />
          <ConnectionStatus />
        </Group>
        </>
        )}
      </Box>

      {liveArOpen && (
        <ArOverlay onClose={handleCloseLiveAr} onError={setArError} />
      )}

      {/* Shown for the moment before the page hops to the App Clip card:
          the card's own copy is English while the iOS button is localized,
          so say which button actually launches AR. */}
      {arLaunchHint && (
        <Box
          style={{
            position: "fixed",
            bottom: "calc(env(safe-area-inset-bottom) + 32px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 700,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            borderRadius: 8,
            padding: "10px 16px",
            maxWidth: "90vw",
          }}
        >
          <Text size="sm" c="white" ta="center">
            Tap &quot;Open&quot; on the card that appears next to launch AR
          </Text>
        </Box>
      )}

      {/* Long-press a connection point and this opens: modules that can go
          there, tried on in place. */}
      <ConnectShelf />

      <Modal
        opened={arError !== null}
        onClose={() => setArError(null)}
        title="Could not open AR"
        centered
        size="sm"
      >
        <Text size="sm" mb="md">
          {arError}
        </Text>
        <Group justify="flex-end">
          <Button onClick={() => setArError(null)}>Close</Button>
        </Group>
      </Modal>

      <Modal
        opened={clearModalOpen}
        onClose={() => setClearModalOpen(false)}
        title="Clear All Modules"
        centered
        size="sm"
      >
        <Text size="sm" mb="md">
          Are you sure you want to remove all {instances.length} module(s) from the scene?
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => setClearModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleClearAll}>
            Clear All
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={newSessionModalOpen}
        onClose={() => setNewSessionModalOpen(false)}
        title="Start New Session"
        centered
        size="sm"
      >
        <Text size="sm" mb="md">
          This opens a new, empty session with its own URL. Your current session stays where it
          is — save or export first if you want to keep this work.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => setNewSessionModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleNewSession}>New Session</Button>
        </Group>
      </Modal>

      <PublishModal
        opened={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        moduleCount={instances.length}
      />
    </>
  );
}
export { Header };
